import React, { useRef } from 'react'
import { Field, Advanced } from '../../lib.jsx'

// Edytor strukturalny schematu blokowego (kind === 'flowchart') — używany zamiast surowego JSON-a
// wewnątrz UnitDetailModal (patrz PageViewerEditor.jsx). Zakres celowo ograniczony: brak live-preview
// SVG w edytorze (wymagałby wywołania backendu do wyrenderowania lub duplikacji layoutu warstwowego
// z app/pdfgen/flowchart_svg.py po stronie JS) — zamiast tego sekcja <Advanced> pokazuje surowy JSON
// wynikowej treści, żeby autor mógł wizualnie zweryfikować strukturę przed zapisem; pełny podgląd
// pojawia się i tak natychmiast po zapisaniu, na podglądzie strony w edytorze.
//
// Generowanie id węzła: w tym kodzie nie ma nigdzie ustalonej konwencji na generowanie id po stronie
// klienta (sprawdzone — brak Date.now()/Math.random()/crypto.randomUUID gdzie indziej), więc używamy
// prostego, deterministycznego licznika sekwencyjnego (node_1, node_2, …), omijając już zajęte id.

const SHAPE_PL = { rect: 'prostokąt', rounded: 'zaokrąglony', diamond: 'romb' }
const ICON_PL = { '': 'brak', check: '✓ check', fail: '✗ fail' }
// Rozmiar (compact/normal/enlarged) jest teraz dobierany AUTOMATYCZNIE przez backend na podstawie
// realnego, zmierzonego renderu strony (patrz app/pdfgen_worker.py::_pdfgen_auto_size_flowcharts) —
// zero ręcznej ingerencji wymaganej. To pole to WYŁĄCZNIE opcjonalny wentyl bezpieczeństwa dla
// eksperta, który chce świadomie wymusić konkretny rozmiar (np. bo wie coś, czego pomiar geometryczny
// nie wie) — zapisywane jako `content.size_override`, ODDZIELNIE od `content.size` (gdyby kiedyś
// backend zaczął persystować automatyczną decyzję), żeby jawny wybór nigdy nie został po cichu
// nadpisany przez automat i odwrotnie.
const SIZE_OVERRIDE_PL = { '': 'Automatyczny (zalecane)', compact: 'Kompaktowy', normal: 'Normalny', enlarged: 'Powiększony' }

function nextId(prefix, existingIds) {
  let i = existingIds.length + 1
  while (existingIds.includes(`${prefix}_${i}`)) i += 1
  return `${prefix}_${i}`
}

export default function FlowchartEditor({ content, onChange }) {
  const counterRef = useRef(0)
  const orientation = content.orientation === 'horizontal' ? 'horizontal' : 'vertical'
  const nodes = Array.isArray(content.nodes) ? content.nodes : []
  const edges = Array.isArray(content.edges) ? content.edges : []

  const set = (patch) => onChange({ ...content, ...patch })

  const addNode = () => {
    counterRef.current += 1
    const id = nextId('node', nodes.map(n => n.id))
    set({ nodes: [...nodes, { id, label: 'Nowy blok', shape: 'rect', fill: '#3E7C8C', text_color: '#ffffff' }] })
  }
  const updateNode = (idx, patch) => set({ nodes: nodes.map((n, i) => i === idx ? { ...n, ...patch } : n) })
  const removeNode = (idx) => {
    const removedId = nodes[idx]?.id
    set({
      nodes: nodes.filter((_, i) => i !== idx),
      edges: edges.filter(e => e.from !== removedId && e.to !== removedId),
    })
  }

  const addEdge = () => {
    if (nodes.length < 2) return
    set({ edges: [...edges, { from: nodes[0].id, to: nodes[1].id, label: '', icon: '', color: '#5B6570' }] })
  }
  const updateEdge = (idx, patch) => set({ edges: edges.map((e, i) => i === idx ? { ...e, ...patch } : e) })
  const removeEdge = (idx) => set({ edges: edges.filter((_, i) => i !== idx) })

  return (
    <div>
      <Field label="Kierunek diagramu">
        <select value={orientation} onChange={e => set({ orientation: e.target.value })}>
          <option value="vertical">Pionowo</option>
          <option value="horizontal">Poziomo</option>
        </select>
      </Field>

      <Field label={`Bloki (${nodes.length})`} hint="Kolejność nie ma znaczenia — układ wyznaczają strzałki (od źródeł do ujść).">
        {nodes.map((n, idx) => (
          <div key={n.id} className="unit-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 120 }} value={n.label || ''} placeholder="Etykieta bloku"
              onChange={e => updateNode(idx, { label: e.target.value })} />
            <select value={n.shape || 'rect'} onChange={e => updateNode(idx, { shape: e.target.value })}>
              {Object.entries(SHAPE_PL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="small muted">Wypełnienie
              <input type="color" value={n.fill || '#3E7C8C'} onChange={e => updateNode(idx, { fill: e.target.value })} />
            </label>
            <label className="small muted">Tekst
              <input type="color" value={n.text_color || '#ffffff'} onChange={e => updateNode(idx, { text_color: e.target.value })} />
            </label>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => removeNode(idx)}>Usuń</button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={addNode} style={{ marginTop: 6 }}>+ Dodaj blok</button>
      </Field>

      <Field label={`Strzałki (${edges.length})`} hint='Etykieta + ikona "TAK"/"NIE" na gałęzi decyzyjnej (romb).'>
        {edges.map((e, idx) => (
          <div key={idx} className="unit-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={e.from || ''} onChange={ev => updateEdge(idx, { from: ev.target.value })}>
              {nodes.map(n => <option key={n.id} value={n.id}>{n.label || n.id}</option>)}
            </select>
            <span className="muted small">→</span>
            <select value={e.to || ''} onChange={ev => updateEdge(idx, { to: ev.target.value })}>
              {nodes.map(n => <option key={n.id} value={n.id}>{n.label || n.id}</option>)}
            </select>
            <input style={{ minWidth: 90 }} value={e.label || ''} placeholder="np. TAK / NIE"
              onChange={ev => updateEdge(idx, { label: ev.target.value })} />
            <select value={e.icon || ''} onChange={ev => updateEdge(idx, { icon: ev.target.value || null })}>
              {Object.entries(ICON_PL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="small muted">Kolor
              <input type="color" value={e.color || '#5B6570'} onChange={ev => updateEdge(idx, { color: ev.target.value })} />
            </label>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => removeEdge(idx)}>Usuń</button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" disabled={nodes.length < 2} onClick={addEdge} style={{ marginTop: 6 }}>
          + Dodaj strzałkę
        </button>
        {nodes.length < 2 && <div className="muted small" style={{ marginTop: 4 }}>Dodaj co najmniej 2 bloki, aby móc je połączyć.</div>}
      </Field>

      <Advanced label="Zaawansowane">
        <Field label="Wymuszony rozmiar (opcjonalnie, zwykle niepotrzebne — system dobiera automatycznie)">
          <select value={content.size_override || ''} onChange={e => set({ size_override: e.target.value || null })}>
            {Object.entries(SIZE_OVERRIDE_PL).map(([v, l]) => <option key={v || 'auto'} value={v}>{l}</option>)}
          </select>
        </Field>
        <div className="muted small" style={{ marginTop: 12, marginBottom: 4 }}>Podgląd JSON (surowa treść zapisu)</div>
        <pre className="mono small" style={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify({ type: 'flowchart', orientation, nodes, edges, size_override: content.size_override || null }, null, 2)}
        </pre>
      </Advanced>
    </div>
  )
}
